import { AxiosRequestConfig, AxiosResponse } from 'axios';
import {
    type IToolUserPatchParams,
    toolUserBalance,
    toolUserCreate,
    toolUserDelete,
    toolUserDetailList,
    toolUserList,
    toolUserPatch,
    toolUserSendActivationEmail,
    toolUserSendApprovedNotification,
    toolUserSendVerifyEmail,
    toolUserUpdate,
} from '@/api/toolUser';
import { AppError } from '@/errors/appError';
import type { ToolUserItem } from '@/models/toolUser';
import { baseListService, isRequestCanceled } from '@/services/baseService';
import { IListParams, IToolUserActionParams, IToolUserApiListResponse, IToolUserFilters, IToolUserMutationResponse, IToolUserSaveParams } from '@/types';
import { IToolUserBalanceResponse } from '@/types/api';
import type { IToolUserDetailFilters } from '@/types/toolUserDetail';

export const toolUserService = {
    async list<T extends object = IToolUserFilters>(params: IListParams<T>): Promise<AxiosResponse<IToolUserApiListResponse>> {
        try {
            return await baseListService<IToolUserApiListResponse>(toolUserList, params);
        } catch (e) {
            if (isRequestCanceled(e)) throw e;
            console.error('Tool user API Error:', e);
            throw new AppError(`Unable to load tool user list, request error`, { cause: e });
        }
    },

    async detailList(params: IListParams<IToolUserDetailFilters>): Promise<AxiosResponse<IToolUserApiListResponse>> {
        try {
            return await baseListService<IToolUserApiListResponse>(toolUserDetailList, params);
        } catch (e) {
            if (isRequestCanceled(e)) throw e;
            console.error('Tool user detail API Error:', e);
            throw new AppError(`Unable to load tool user detail list, request error`, { cause: e });
        }
    },

    async save(item: ToolUserItem): Promise<AxiosResponse<IToolUserMutationResponse>> {
        const row = item.toApiData();
        if (item.id) {
            return this.update({ rows: [row] });
        }
        return this.create({ rows: [row] });
    },

    async update(params: IToolUserSaveParams): Promise<AxiosResponse<IToolUserMutationResponse>> {
        try {
            return await toolUserUpdate(params);
        } catch (e) {
            console.error('Tool user update API Error:', e);
            throw new AppError(`Unable to update tool user, request error`, { cause: e });
        }
    },

    async create(params: IToolUserSaveParams): Promise<AxiosResponse<IToolUserMutationResponse>> {
        try {
            return await toolUserCreate(params);
        } catch (e) {
            console.error('Tool user create API Error:', e);
            throw new AppError(`Unable to create tool user, request error`, { cause: e });
        }
    },

    async patch(params: IToolUserPatchParams): Promise<AxiosResponse<IToolUserMutationResponse>> {
        try {
            return await toolUserPatch(params);
        } catch (e) {
            console.error('Tool user patch API Error:', e);
            throw new AppError(`Unable to patch tool user, request error`, { cause: e });
        }
    },

    async sendVerifyEmail(params: IToolUserActionParams): Promise<AxiosResponse<IToolUserMutationResponse>> {
        try {
            return await toolUserSendVerifyEmail(params);
        } catch (e) {
            console.error('Tool user sendVerifyEmail API Error:', e);
            throw new AppError(`Unable to send verify email, request error`, { cause: e });
        }
    },

    async sendActivationEmail(params: IToolUserActionParams): Promise<AxiosResponse<IToolUserMutationResponse>> {
        try {
            return await toolUserSendActivationEmail(params);
        } catch (e) {
            console.error('Tool user sendActivationEmail API Error:', e);
            throw new AppError(`Unable to send activation email, request error`, { cause: e });
        }
    },

    async sendApprovedNotification(params: IToolUserActionParams): Promise<AxiosResponse<IToolUserMutationResponse>> {
        try {
            return await toolUserSendApprovedNotification(params);
        } catch (e) {
            console.error('Tool user sendApprovedNotification API Error:', e);
            throw new AppError(`Unable to send approval notification, request error`, { cause: e });
        }
    },

    async delete(params: IToolUserActionParams): Promise<AxiosResponse<{ success: boolean }>> {
        try {
            return await toolUserDelete(params);
        } catch (e) {
            console.error('Tool user delete API Error:', e);
            throw new AppError(`Unable to delete tool user, request error`, { cause: e });
        }
    },

    async getBalance(toolUserId: number, config?: AxiosRequestConfig): Promise<AxiosResponse<IToolUserBalanceResponse>> {
        try {
            return await toolUserBalance(toolUserId, config);
        } catch (e) {
            if (isRequestCanceled(e)) throw e;
            console.error('Tool user balance API Error:', e);
            throw new AppError('Unable to load tool user balance, request error', { cause: e });
        }
    },
};
